import { KeyboardArrowUp, KeyboardArrowDown } from '@mui/icons-material';
import { Box, Grid, LinearProgress, Tooltip } from '@mui/material';
import { useEffect, useState, useCallback, useRef } from 'react';
import { isMobile, isMobileOnly, useMobileOrientation } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getData, setCheckedArchivePictures } from '../../api/dataSlice';
import { useLazyGetTagsQuery } from '../../api/graphqlApi';
import { approachNumber, isEmpty } from '../Utilities';
import { useIsOverflow } from '../hooks/useIsOverflow';
import dayjs from 'dayjs';
import InfiniteScroll from 'react-infinite-scroll-component';
import OverviewImage from './OverviewImage';

export default function OverviewGrid(props) {
    
    const { handleOverviewClick, showOverview, imgHeight, cameraId, cameraUrl, imagesList, imageIndex, contentHeight, fullscreenHandle, onImageSelected, fallbackSource } = props;
    const { t } = useTranslation();
    const { isLandscape } = useMobileOrientation();
    const { overViewCameraId } = useSelector(getData);
    const { CAMERAS } = window.conf;

    const [scrollImages, setScrollImages] = useState([]);
    const [hasMoreValue, setHasMoreValue] = useState(true);
    const [imageDate, setImageDate] = useState(null);
    const [todayImages, setTodayImages] = useState([]);
    const [isAtScrollStart, setIsAtScrollStart] = useState(false);
    const [getTags] = useLazyGetTagsQuery();
    const [taggedArchivePictureIds, setTaggedArchivePictureIds] = useState([])

    const dispatch = useDispatch();
    const numImagesToLoadAtScroll = 20;
    const comparisonDateFormat = "YYYY-MM-DD";
    const databaseDateFormat = 'YYYY-MM-DDTHH:mm:00.000[Z]'
    const cameraName = CAMERAS[cameraId].name
    const camCount = CAMERAS.length
    const ref = useRef()
    const isOverflow = useIsOverflow(ref)
    
    // add resize event listener to close overview when browser size changes
    useEffect(() => {
        window.addEventListener('resize', closeOverview)
        return () => window.removeEventListener('resize', closeOverview)
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    function closeOverview () {
        if (showOverview) {
            handleOverviewClick()
        }
    }
    
    // Track changes on current image index and date
    useEffect(() => {
        const timestamp = dayjs(imagesList?.[0]?.[imageIndex]?.[0])
        const timestampDatabase = timestamp.format(databaseDateFormat)
        const timestampComparison = timestampDatabase.slice(0,10) // databaseDateFormat to comparisonDateFormat

        setImageDate(timestampComparison)
        getTaggedArchivePictureIds(timestampComparison)

    }, [imagesList, imageIndex]); // eslint-disable-line react-hooks/exhaustive-deps
    
    const getTaggedArchivePictureIds = useCallback(async(date) => {
        try {
            let tags = await getTags({ date: date, index: cameraId })
            let tagList = tags.data.map(tag => tag.archivePicturesId)
            let tagListWithoutDuplicates = [...new Set(tagList)]

            setTaggedArchivePictureIds(tagListWithoutDuplicates)
        } catch (error) {
            console.error(error)
        }
    }, [cameraId, getTags])
    
    // If another overview gets open then close this overview component
    useEffect(() => {
        if (overViewCameraId !== cameraId) {
            onImageSelected(null, true); // do not change the index and only hide the overview
        }
    }, [overViewCameraId]) // eslint-disable-line react-hooks/exhaustive-deps

    // If user changes the date then empty scrollView and load new images in today images array
    useEffect(() => {
        if (imageDate != null) {
            setScrollImages([]);
            setHasMoreValue(true);
            const selectedImageDate = dayjs(imageDate);
            let imagesOfDate = imagesList?.[1]?.[selectedImageDate.$y]?.[selectedImageDate.$M]?.[selectedImageDate.$D];
            let imagesWithIndex = [];
            let imageHours = !isEmpty(imagesOfDate) && Object.keys(imagesOfDate);
            for (let h = 0; h < imageHours?.length; h++) {
                const minutes = Object.keys(imagesOfDate[imageHours[h]]);
                for (let m = 0; m < minutes.length; m++) {
                    const image = imagesOfDate[imageHours[h]][minutes[m]];
                    const hourKey = Object.keys(image)[0];
                    const imageIndex = image[hourKey];
                    imagesWithIndex.push({ image: imagesList?.[0]?.[imageIndex], index: imageIndex });
                }
            }
            setTodayImages(imagesWithIndex);
        }
    }, [imageDate]); // eslint-disable-line react-hooks/exhaustive-deps

    // Load images into scrollview if today's images list changes
    useEffect(() => {
        if (todayImages.length > 0) {
            todayImages.length <= numImagesToLoadAtScroll ? setHasMoreValue(false) : setHasMoreValue(true);
            loadScrollViewData();
        }
    }, [todayImages]) // eslint-disable-line react-hooks/exhaustive-deps

    //Load more images if user has scrolled the visibile images
    const handleOnRowsScrollEnd = () => {
        if (scrollImages.length < todayImages.length) {
            setHasMoreValue(true);
            loadScrollViewData();
        } else {
            setHasMoreValue(false);
        }
    };

    // Get a slice of data from all images list
    const loadScrollViewData = () => {
        const startIndex = scrollImages.length;
        const endIndex = (startIndex + numImagesToLoadAtScroll) < todayImages.length ? startIndex + numImagesToLoadAtScroll : todayImages.length;
        const imagesChunk = todayImages.slice(startIndex, endIndex);
        setScrollImages([...scrollImages, ...imagesChunk]);
    };


    const hasPreviousDayImages = () => {
        if (imageDate) {
            const prevDate = dayjs(imageDate).subtract(1, 'day');
            const lastDayImages = imagesList?.[1]?.[prevDate.$y]?.[prevDate.$M]?.[prevDate.$D];
            return lastDayImages !== undefined;
        } else {
            return false
        }
    }
    
    function onScrollChangeListener() {
        let scrollDiv = document.getElementById(`infinite-scroll-${cameraId}`);
        let scrollTop = scrollDiv?.scrollTop;

        // check if user has scrolled to the start of scrollview container
        if (scrollTop > -30) {
            setIsAtScrollStart(true)
        } else {
            setIsAtScrollStart(false)
        }
    }

    function changeIndexByTimestamp(timestamp) {
        if (imagesList && timestamp) {
            let dict = imagesList[1];
            let indexes = [timestamp.$y, timestamp.$M, timestamp.$D, timestamp.$H, timestamp.$m] // split timestamp in parts
            // iterate through dictionary
            for (let i = 0; i < indexes.length; i++) {
                const elem = indexes[i]
                if (dict[elem]) {
                    dict = dict[elem]
                }
            }
            let newIndex = dict[timestamp.$m];
            // check if minute of timestamp is an available key of the dictionary, otherwise search for prev/next minute in keys
            if (Number.isFinite(newIndex)) {
                newIndex = dict[timestamp.$m]
            } else {
                var minutes = Object.keys(dict)
                var goal = timestamp.$m
                var closest = approachNumber(minutes, goal)
                newIndex = dict[closest][closest]
            }
            if (Number.isFinite(newIndex)) {
                // select new image index and do not hide the overview
                onImageSelected(newIndex, false);
            }
        }
    }

    const renderOverviewImage = (image, index) => {
        let timestamp = image[0]
        let archivePicture = image[1][cameraId]
        let isSelected = imageIndex === index
        let isTagged = Boolean(taggedArchivePictureIds.find(id => id === archivePicture.id)) // check if img have index (id) equal to one of the tagged img ids 

        if (!!archivePicture) {
            return (
                <OverviewImage
                    key={index}
                    onImageSelected={onImageSelected}
                    timestamp={timestamp}
                    index={index}
                    cameraUrl={cameraUrl}
                    isSelected={isSelected}
                    isTagged={isTagged}
                    archivePicture={archivePicture}
                    fallbackSource={fallbackSource}
                />
            )
        }
    }

    const navButtonSx = {
        position: 'absolute',
        width: 40, height: 40, borderRadius: '50%',
        bgcolor: 'rgba(43, 51, 63, 0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', zIndex: 2,
        '&:hover': { bgcolor: 'rgba(43, 51, 63, 0.9)' }
    }

    const renderFullScreenDateSwitch = () => (
        <>
            {hasPreviousDayImages() && (
                <Tooltip title={t("date.previous_day")} arrow>
                    <Box
                        onClick={() => {
                            // decrement date by 1 day
                            let newDate = dayjs(imageDate).subtract(1, 'day').format(comparisonDateFormat);
                            let timestamp = dayjs(imagesList?.[0]?.[imageIndex]?.[0]).set('date', dayjs(newDate).$D).set('month', dayjs(newDate).$M);
                            changeIndexByTimestamp(timestamp);
                            dispatch(setCheckedArchivePictures({ type: 'clean' }))
                        }}
                        sx={{ ...navButtonSx, top: cameraName ? 37 : 4, right: isOverflow && !isMobile ? 12 : 4 }}
                    >
                        <KeyboardArrowUp sx={{ color: 'white', fontSize: 22 }} />
                    </Box>
                </Tooltip>
            )}

            {imageDate !== dayjs().format(comparisonDateFormat) && isAtScrollStart && (
                <Tooltip placement='top' title={t("date.next_day")} arrow>
                    <Box
                        onClick={() => {
                            // increment date by 1 day
                            let newDate = dayjs(imageDate).add(1, 'day').format(comparisonDateFormat);
                            let timestamp = dayjs(imagesList?.[0]?.[imageIndex]?.[0]).set('date', dayjs(newDate).$D).set('month', dayjs(newDate).$M);
                            changeIndexByTimestamp(timestamp);
                            dispatch(setCheckedArchivePictures({ type: 'clean' }))
                        }}
                        sx={{ ...navButtonSx, bottom: 33, right: isOverflow && !isMobile ? 12 : 4 }}
                    >
                        <KeyboardArrowDown sx={{ color: 'white', fontSize: 22 }} />
                    </Box>
                </Tooltip>
            )}
        </>
    )

    const subtractHeight = cameraName ? 69 : 36

    return (
        <Box sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: "33px",
            mt: cameraName ? "36px" : "3px",
            height: fullscreenHandle.active
                ? "100%"
                : isMobileOnly
                    ? isLandscape
                        ? imgHeight - subtractHeight
                        : camCount < 2
                            ? contentHeight - subtractHeight
                            : camCount < 3
                                ? (contentHeight / 2) - subtractHeight
                                : imgHeight - subtractHeight
                    : imgHeight - subtractHeight
            }}
        >
            <Box
                ref={ref}
                id={`infinite-scroll-${cameraId}`}
                className="thin-scrollbar"
                sx={{
                    height: '100%',
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column'
                }}
                onScroll={onScrollChangeListener}
            >
                <InfiniteScroll
                    dataLength={scrollImages.length}
                    next={handleOnRowsScrollEnd}
                    hasMore={hasMoreValue}
                    scrollThreshold={0.8}
                    scrollableTarget={`infinite-scroll-${cameraId}`}
                    loader={<LinearProgress />}
                    style={{ overflow: "unset" }}
                >
                    <Box sx={{ flexGrow: 1 }}>

                        <Grid
                            container
                            spacing={0.5}
                            columns={{ xs: 4, sm: 8, md: 12 }}
                            direction="row"
                        >

                            {/* render image items from the scroll data */}
                            {scrollImages.map(elem => renderOverviewImage(elem.image, elem.index))}

                        </Grid>

                    </Box>
                </InfiniteScroll>
            </Box>

            {renderFullScreenDateSwitch()}

        </Box>
    )
}